import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Password input with a built-in eye toggle.
 * Renders identical look-and-feel to the existing `.auth-input` text fields
 * (Login / Register / Reset Password) and adds an eye button on the right
 * that toggles between obscured and revealed text.
 *
 * Props are intentionally minimal — everything else is forwarded to the
 * underlying <input/> so callers can keep using their familiar API.
 */
export const PasswordInput = React.forwardRef(function PasswordInput(
  { id, value, onChange, placeholder = '••••••••', autoComplete = 'current-password', dataTestId, className = 'auth-input', ...rest },
  ref,
) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={ref}
        id={id}
        type={revealed ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={dataTestId}
        className={className}
        style={{ paddingRight: 48 }}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        data-testid={dataTestId ? `${dataTestId}-toggle` : 'password-toggle'}
        tabIndex={-1}
        style={{
          position: 'absolute',
          top: '50%',
          right: 12,
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          padding: 6,
          cursor: 'pointer',
          color: '#5A5A5A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {revealed ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
      </button>
    </div>
  );
});

export default PasswordInput;
